import Axhelper from '@/lib/Axhelper'

class MiastoService {
	constructor() {
		this.baseUrl = window.RUNTIME_CONFIG.API_URL
		this.endpoints = {
			miastoAll: '/api/miasto/filters',
			miastoDetails: '/api/miasto/details',
			miastoSearch: '/api/miasto/filters',
			miastoCreate: '/api/miasto',
			miastoUpdate: '/api/miasto',
			miastoDelete: '/api/miasto',
		}
	}

	async getAll(options = {}) {
		try {
			const {
				limit = 50,
				sortColumn = '_id',
				sortDirection = 'desc',
				filters = {},
			} = options

			const params = new URLSearchParams({
				sortcol: sortColumn,
				sortdir: sortDirection,
				limit: String(limit),
			})
			if (filters?.id_powiat) {
				params.append('id_powiat', String(filters.id_powiat))
			}
			if (filters?.id_wojewodztwo) {
				params.append('id_wojewodztwo', String(filters.id_wojewodztwo))
			}
			if (filters?.id_gmina) {
				params.append('id_gmina', String(filters.id_gmina))
			}
			const url = `${this.endpoints.miastoAll}?${params.toString()}`
			const response = await Axhelper.sendGetAsync(url)

			if (response.error) {
				return {
					data: [],
					error: response.error,
					totalCount: 0,
					hasMore: false,
				}
			}

			const totalCount = response.headers?.['x-total-count']
				? parseInt(response.headers['x-total-count'], 10)
				: response.data?.length || 0

			return {
				data: response.data || [],
				error: null,
				totalCount,
				hasMore: (response.data?.length || 0) < totalCount,
				headers: response.headers,
			}
		} catch (error) {
			console.error('MiastoService.getAll error:', error)
			return {
				data: [],
				error: error.message || 'Failed to fetch miasta',
				totalCount: 0,
				hasMore: false,
			}
		}
	}

	async getById(miastoId) {
		try {
			if (!miastoId) {
				return {
					data: null,
					error: 'Invalid miasto ID provided',
				}
			}

			const url = `${this.endpoints.miastoDetails}/${encodeURIComponent(miastoId)}`
			const response = await Axhelper.sendGetAsync(url)

			if (response.error) {
				return {
					data: null,
					error: response.error,
				}
			}

			return {
				data: response.data || null,
				error: null,
			}
		} catch (error) {
			console.error('MiastoService.getById error:', error)
			return {
				data: null,
				error: error.message || 'Failed to fetch miasto details',
			}
		}
	}

	async search(searchTerm, options = {}) {
		try {
			if (!searchTerm) {
				return {
					data: [],
					error: 'Invalid search term provided',
					totalCount: 0,
					hasMore: false,
				}
			}

			const {
				limit = 50,
				sortColumn = '_id',
				sortDirection = 'desc',
				filters = {},
			} = options

			const params = new URLSearchParams({
				sortcol: sortColumn,
				sortdir: sortDirection,
				limit: String(limit),
			})
			if (filters?.id_powiat) {
				params.append('id_powiat', String(filters.id_powiat))
			}
			if (filters?.id_wojewodztwo) {
				params.append('id_wojewodztwo', String(filters.id_wojewodztwo))
			}
			if (filters?.id_gmina) {
				params.append('id_gmina', String(filters.id_gmina))
			}
			const url = `${this.endpoints.miastoSearch}?searchText=${encodeURIComponent(searchTerm)}&${params.toString()}`
			const response = await Axhelper.sendGetAsync(url)

			if (response.error) {
				return {
					data: [],
					error: response.error,
					totalCount: 0,
					hasMore: false,
				}
			}

			const totalCount = response.headers?.['x-total-count']
				? parseInt(response.headers['x-total-count'], 10)
				: response.data?.length || 0

			return {
				data: response.data || [],
				error: null,
				totalCount,
				hasMore: (response.data?.length || 0) < totalCount,
				headers: response.headers,
			}
		} catch (error) {
			console.error('MiastoService.search error:', error)
			return {
				data: [],
				error: error.message || 'Failed to search miasta',
				totalCount: 0,
				hasMore: false,
			}
		}
	}

	async create(miastoData) {
		try {
			const response = await Axhelper.sendPostAsync(this.endpoints.miastoCreate, miastoData)

			if (response.error) {
				return {
					success: false,
					error: response.error,
				}
			}

			return {
				success: true,
				error: null,
				data: response.data || null,
			}
		} catch (error) {
			console.error('MiastoService.create error:', error)
			return {
				success: false,
				error: error.message || 'Failed to create miasto',
			}
		}
	}

	async update(miastoId, miastoData) {
		try {
			if (!miastoId) {
				return {
					success: false,
					error: 'Invalid miasto ID provided',
				}
			}

			const url = `${this.endpoints.miastoUpdate}/${encodeURIComponent(miastoId)}`
			const response = await Axhelper.sendPutAsync(url, miastoData)

			if (response.error) {
				return {
					success: false,
					error: response.error,
				}
			}

			return {
				success: true,
				error: null,
				data: response.data || null,
			}
		} catch (error) {
			console.error('MiastoService.update error:', error)
			return {
				success: false,
				error: error.message || 'Failed to update miasto',
			}
		}
	}

	async delete(miastoId) {
		try {
			if (!miastoId) {
				return {
					success: false,
					error: 'Invalid miasto ID provided',
					message: null,
				}
			}

			const url = `${this.endpoints.miastoDelete}/${encodeURIComponent(miastoId)}`
			const response = await Axhelper.sendDeleteAsync(url)

			if (response.error) {
				return {
					success: false,
					error: response.error,
					message: null,
				}
			}

			return {
				success: true,
				error: null,
				message: response.data || 'Miasto deleted successfully',
			}
		} catch (error) {
			console.error('MiastoService.delete error:', error)
			return {
				success: false,
				error: error.message || 'Failed to delete miasto',
				message: null,
			}
		}
	}
}

export default new MiastoService()
